/**
 * The plan drawing, as data.
 *
 * Everything the SVG renders is derived here, in model millimetres, so that
 * "is the drawing to scale?" is a question a test can answer without a DOM:
 * the shapes come straight out of the interior domain's own footprint helpers
 * (`benchRunFootprint`, `heaterFootprint`), which are the same functions the
 * BOM and the validator measure with. Nothing in this file re-derives a
 * rectangle from wall coordinates by hand.
 */
import {
	type BenchRun,
	benchRunFootprint,
	type FootprintMm,
	type HeaterSizeMm,
	heaterFootprint,
	type InteriorLayout,
	type InteriorRoom,
	type InteriorWall,
	wallRunsAlongX,
} from "../../../domain/interior";
import {
	hingeAtLowEdge,
	resolveDoorSwing,
	resolveHingeSide,
} from "../../../domain/openings/openingPresets";
import type { OpeningLike } from "../../../domain/openings/validateOpeningSet";
import type { SaunaDimensions } from "../../../engines/types";
import { DEFAULT_PLAN_PADDING_MM } from "./planProjection";

export interface PlanBenchShape {
	run: BenchRun;
	footprint: FootprintMm;
	/** Ends of the run's centre line, for the two resize handles. */
	startHandle: { xMm: number; zMm: number };
	endHandle: { xMm: number; zMm: number };
}

export interface PlanOpeningShape {
	opening: OpeningLike;
	/** The opening's own slot in the wall. */
	footprint: FootprintMm;
	/** Quarter-circle sweep of a door leaf; null for windows. */
	swing: FootprintMm | null;
	/**
	 * The leaf itself, drawn open at 90° from its hinge jamb (issue 160), so
	 * the plan shows which side the door hangs on. Null for windows.
	 */
	leaf: PlanDoorLeaf | null;
	wall: InteriorWall;
}

/** Model-frame points, mm. `closed` is the latch jamb, `open` the leaf tip. */
export interface PlanDoorLeaf {
	hinge: { xMm: number; zMm: number };
	closed: { xMm: number; zMm: number };
	open: { xMm: number; zMm: number };
	radiusMm: number;
}

export interface PlanScene {
	rooms: InteriorRoom[];
	benches: PlanBenchShape[];
	heater: FootprintMm;
	openings: PlanOpeningShape[];
}

/**
 * Centre line of a run, at its two ends. The handles sit ON the run rather
 * than beside it so a touch target never overlaps the neighbouring run's.
 *
 * Exported because the 3D view's end handles (issue 133) must sit at the same
 * two points as the plan view's — a resize grabbed at one end in one surface and
 * the other end in the other surface is the same gesture reading as two.
 */
export function benchRunHandlePointsMm(run: BenchRun, footprint: FootprintMm) {
	const midX = (footprint.minXMm + footprint.maxXMm) / 2;
	const midZ = (footprint.minZMm + footprint.maxZMm) / 2;
	return wallRunsAlongX(run.wall)
		? {
				startHandle: { xMm: footprint.minXMm, zMm: midZ },
				endHandle: { xMm: footprint.maxXMm, zMm: midZ },
			}
		: {
				startHandle: { xMm: midX, zMm: footprint.minZMm },
				endHandle: { xMm: midX, zMm: footprint.maxZMm },
			};
}

/**
 * Where an opening sits in plan, plus the area its leaf sweeps.
 *
 * `xMm` is measured from the low corner of the OUTER wall line, so the
 * building's half-extent is what converts it to the model frame — the same
 * conversion `deriveInteriorDoors` performs.
 */
export function planOpeningShape(
	opening: OpeningLike,
	dimensions: SaunaDimensions,
	swingDepthMm: number,
	wallThicknessMm: number,
): PlanOpeningShape | null {
	if (opening.wall === "divider") return null;
	const wall = opening.wall as InteriorWall;
	const runsAlongX = wallRunsAlongX(wall);

	const halfWidthMm = (dimensions.width / 2) * 1000;
	const halfLengthMm = (dimensions.length / 2) * 1000;
	const halfMm = runsAlongX ? halfWidthMm : halfLengthMm;

	const alongMin = opening.xMm - halfMm;
	const alongMax = alongMin + opening.widthMm;

	const faceMm =
		wall === "back"
			? -halfLengthMm
			: wall === "front"
				? halfLengthMm
				: wall === "left"
					? -halfWidthMm
					: halfWidthMm;
	const inward = wall === "back" || wall === "left" ? 1 : -1;

	const slotNear = faceMm;
	const slotFar = faceMm + inward * wallThicknessMm;
	// Issue 172: a door opening out hangs on the outer face and sweeps the
	// outside; one opening in hangs on the inner face and sweeps the room.
	const opensIn = resolveDoorSwing(opening) === "in";
	const hingeLine = opensIn ? slotFar : faceMm;
	const sweep = opensIn ? inward : -inward;
	const swingFar = hingeLine + sweep * swingDepthMm;

	const box = (near: number, far: number): FootprintMm =>
		runsAlongX
			? {
					minXMm: alongMin,
					maxXMm: alongMax,
					minZMm: Math.min(near, far),
					maxZMm: Math.max(near, far),
				}
			: {
					minXMm: Math.min(near, far),
					maxXMm: Math.max(near, far),
					minZMm: alongMin,
					maxZMm: alongMax,
				};

	// (along, across) → model frame.
	const point = (along: number, across: number) =>
		runsAlongX ? { xMm: along, zMm: across } : { xMm: across, zMm: along };
	let leaf: PlanDoorLeaf | null = null;
	if (opening.type === "door") {
		const lowHinge = hingeAtLowEdge(wall, resolveHingeSide(opening));
		const hingeAlong = lowHinge ? alongMin : alongMax;
		const latchAlong = lowHinge ? alongMax : alongMin;
		leaf = {
			hinge: point(hingeAlong, hingeLine),
			closed: point(latchAlong, hingeLine),
			open: point(hingeAlong, hingeLine + sweep * opening.widthMm),
			radiusMm: opening.widthMm,
		};
	}

	return {
		opening,
		wall,
		footprint: box(slotNear, slotFar),
		swing: opening.type === "door" ? box(hingeLine, swingFar) : null,
		leaf,
	};
}

/** Clear space kept between an outward door leaf and the drawing's edge. */
const OUTWARD_LEAF_MARGIN_MM = 100;

/**
 * Margin the plan needs around the building so an outward door is drawn whole.
 *
 * An outward leaf reaches its own width past the outer wall face, and its
 * swing box reaches `swingDepthMm`. The fixed default padding (700 mm) is
 * narrower than a 800-900 mm front door, which clipped the leaf at zoom 1.
 * Divider doors and inward doors stay inside the building and need nothing.
 */
export function planPaddingMm(
	openings: readonly OpeningLike[],
	swingDepthMm: number,
): number {
	let reachMm = 0;
	for (const opening of openings) {
		if (opening.type !== "door" || opening.wall === "divider") continue;
		if (resolveDoorSwing(opening) === "in") continue;
		reachMm = Math.max(reachMm, opening.widthMm, swingDepthMm);
	}
	return Math.max(
		DEFAULT_PLAN_PADDING_MM,
		reachMm > 0 ? reachMm + OUTWARD_LEAF_MARGIN_MM : 0,
	);
}

export interface BuildPlanSceneInput {
	layout: InteriorLayout;
	room: InteriorRoom;
	relaxRoom: InteriorRoom | null;
	heaterSize: HeaterSizeMm;
	openings: OpeningLike[];
	dimensions: SaunaDimensions;
	swingDepthMm: number;
	wallThicknessMm: number;
}

export function buildPlanScene(input: BuildPlanSceneInput): PlanScene {
	const rooms = input.relaxRoom ? [input.room, input.relaxRoom] : [input.room];

	const benches: PlanBenchShape[] = input.layout.benchRuns.map((run) => {
		const room = rooms.find((entry) => entry.id === run.room) ?? input.room;
		const footprint = benchRunFootprint(run, room);
		return { run, footprint, ...benchRunHandlePointsMm(run, footprint) };
	});

	const openings: PlanOpeningShape[] = [];
	for (const opening of input.openings) {
		const shape = planOpeningShape(
			opening,
			input.dimensions,
			input.swingDepthMm,
			input.wallThicknessMm,
		);
		if (shape) openings.push(shape);
	}

	return {
		rooms,
		benches,
		heater: heaterFootprint(input.layout.heater, input.room, input.heaterSize),
		openings,
	};
}
